import { z } from 'zod';
import { supportedLocales, type Locale } from './locales';

/**
 * Schemas for everything under content/. The CMS edits those files, the build
 * imports them through src/web/lib/content.ts, and every import is parsed with
 * these schemas — so an edit that breaks a schema fails the tests and the
 * build before it can deploy.
 *
 * Every JSON content file is bilingual: `{ "en": {...}, "es": {...} }`, both
 * halves validated against the same schema by `localized()`.
 */

export { supportedLocales, type Locale };

const text = (max = 400) => z.string().trim().min(1).max(max);
const longText = (max = 4000) => z.string().trim().min(1).max(max);

/**
 * A root-relative site path (`/contact`) or a fragment. Protocol-relative
 * (`//host`) and backslash paths are rejected: both are ways to make a
 * "local" link leave the site.
 */
export const sitePath = z
  .string()
  .regex(/^(\/(?![/\\])[^\s\\]*|#[\w-]+)$/, 'Must be a site path starting with "/" (or a #fragment)');

/** Wraps a per-locale schema into the `{ en, es }` file shape. */
export function localized<T extends z.ZodTypeAny>(schema: T) {
  return z.object(Object.fromEntries(supportedLocales.map((locale) => [locale, schema])) as Record<Locale, T>);
}

export const pageMetaSchema = z.object({
  /** The `<title>` (the site name is appended automatically). */
  title: text(90),
  /** The meta description and the Open Graph description. */
  description: text(300),
});

export const settingsSchema = z.object({
  siteName: text(60),
  tagline: text(120),
  /** Shown in the footer and on every page that describes availability. */
  statusNotice: text(300),
  footerNote: text(300),
});

const itemSchema = z.object({ title: text(120), body: longText(800) });

/** Whether a capability is being built now, or only planned for later. */
export const capabilityStatus = z.enum(['in-development', 'planned']);
export type CapabilityStatus = z.infer<typeof capabilityStatus>;

export const homeSchema = z.object({
  meta: pageMetaSchema,
  hero: z.object({
    eyebrow: text(80),
    headline: text(140),
    subhead: longText(400),
    primaryCtaLabel: text(40),
    secondaryCtaLabel: text(40),
  }),
  foundation: z.object({ title: text(120), intro: longText(600), items: z.array(itemSchema).min(1).max(12) }),
  planned: z.object({ title: text(120), intro: longText(600), items: z.array(itemSchema).min(1).max(12) }),
  closing: z.object({ title: text(120), body: longText(600), ctaLabel: text(40) }),
});

export const productSchema = z.object({
  meta: pageMetaSchema,
  headline: text(140),
  intro: longText(800),
  sections: z
    .array(
      z.object({
        id: z.string().regex(/^[a-z0-9-]+$/, 'Use lowercase letters, digits and dashes'),
        title: text(120),
        status: capabilityStatus,
        body: longText(1200),
        points: z.array(text(300)).max(10).default([]),
      }),
    )
    .min(1)
    .max(20)
    .superRefine((sections, context) => {
      // Section IDs are the page's #anchors (and the language switch keeps the #fragment), so each must be unique.
      const seen = new Set<string>();
      sections.forEach((section, index) => {
        if (seen.has(section.id)) {
          context.addIssue({
            code: 'custom',
            path: [index, 'id'],
            message: `Section ID "${section.id}" is used more than once; each section needs its own. / El ID de sección "${section.id}" se repite; cada sección necesita uno propio.`,
          });
        }
        seen.add(section.id);
      });
    }),
  closing: z.object({ title: text(120), body: longText(600) }),
});

export const getStartedSchema = z.object({
  meta: pageMetaSchema,
  headline: text(140),
  unavailable: z.object({ title: text(120), body: longText(1200), contactCtaLabel: text(40) }),
  available: z.object({ title: text(120), body: longText(1200), signupLabel: text(40), loginLabel: text(40) }),
});

export const contactSchema = z.object({
  meta: pageMetaSchema,
  headline: text(140),
  intro: longText(800),
  /** Shown instead of the form when the contact endpoint is not configured. */
  notConfigured: z.object({ title: text(120), body: longText(800) }),
  success: z.object({ title: text(120), body: longText(400) }),
  privacyNote: longText(600),
});

export const faqItemSchema = z.object({
  question: text(200),
  /** Plain text. Blank lines separate paragraphs. */
  answer: longText(2000),
});

export const faqSchema = z.object({
  meta: pageMetaSchema,
  headline: text(140),
  intro: longText(600),
  items: z.array(faqItemSchema).min(1).max(40),
});

export const localizedSettingsSchema = localized(settingsSchema);
export const localizedHomeSchema = localized(homeSchema);
export const localizedProductSchema = localized(productSchema).superRefine((product, context) => {
  // The language switch keeps the #fragment, so /product#rent and /es/product#rent must both exist.
  const english = product.en.sections.map((section) => section.id);
  for (const locale of supportedLocales) {
    const ids = product[locale].sections.map((section) => section.id);
    if (ids.join('\n') !== english.join('\n')) {
      context.addIssue({
        code: 'custom',
        path: [locale, 'sections'],
        message: `Section IDs must be the same, in the same order, in every language (English: ${english.join(', ')}). / Los ID de sección deben ser los mismos y en el mismo orden en todos los idiomas (inglés: ${english.join(', ')}).`,
      });
    }
  }
});
export const localizedGetStartedSchema = localized(getStartedSchema);
export const localizedContactSchema = localized(contactSchema);
export const localizedFaqSchema = localized(faqSchema);

export type Settings = z.infer<typeof settingsSchema>;
export type HomeContent = z.infer<typeof homeSchema>;
export type ProductContent = z.infer<typeof productSchema>;
export type GetStartedContent = z.infer<typeof getStartedSchema>;
export type ContactContent = z.infer<typeof contactSchema>;
export type FaqContent = z.infer<typeof faqSchema>;
export type FaqItem = z.infer<typeof faqItemSchema>;

/** A real calendar date written as YYYY-MM-DD (2026-02-31 is rejected). */
export function isCalendarDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

export const calendarDate = z.string().refine(isCalendarDate, 'Use a real date as YYYY-MM-DD');

/** Slugs become URL segments: lowercase ASCII letters and digits separated by single dashes. */
export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Documentation articles: content/docs/<slug>.md (English) and
 * <slug>.es.md (Spanish). Flat YAML frontmatter, as the CMS writes it.
 *
 * `availability` is shown as a badge on every article, so a reader can
 * never mistake a description of the foundation for a launched feature.
 */
export const docAvailability = z.enum(['in-development', 'not-yet-available']);
export type DocAvailability = z.infer<typeof docAvailability>;

export const docFrontmatterSchema = z.object({
  title: text(90),
  description: text(300),
  /** Position in the docs index (ascending). */
  order: z.coerce.number().int().min(0).max(999),
  availability: docAvailability,
  /** YYYY-MM-DD of the last substantive change. */
  updated: calendarDate,
});
export type DocFrontmatter = z.infer<typeof docFrontmatterSchema>;

/**
 * Legal pages: content/legal/<slug>.md and <slug>.es.md. Every page is a
 * draft until the owner approves it: `status: draft` shows a "Draft — not yet
 * in effect" banner and keeps the page out of the sitemap's indexable set.
 */
export const legalSlugs = ['privacy', 'terms', 'cookies', 'ai-disclosure', 'delete-account'] as const;
export type LegalSlug = (typeof legalSlugs)[number];

export const OWNER_APPROVAL_MARKER = 'TODO(owner-approval)';

export const legalFrontmatterSchema = z
  .object({
    title: text(90),
    description: text(300),
    status: z.enum(['draft', 'approved']),
    /** "TODO(owner-approval)" while a draft; an approved page needs a real date. */
    effectiveDate: z.union([z.literal(OWNER_APPROVAL_MARKER), calendarDate]),
    updated: calendarDate,
  })
  .superRefine((page, context) => {
    if (page.status === 'approved' && page.effectiveDate === OWNER_APPROVAL_MARKER) {
      context.addIssue({
        code: 'custom',
        path: ['effectiveDate'],
        message: 'An approved legal page needs a real effective date (YYYY-MM-DD)',
      });
    }
  });

/** Every string in a frontmatter value, with the field path it came from. */
function stringFields(value: unknown, path: string): Array<[string, string]> {
  if (typeof value === 'string') return [[path, value]];
  if (Array.isArray(value)) return value.flatMap((item, index) => stringFields(item, `${path}[${index}]`));
  if (value && typeof value === 'object') {
    return Object.entries(value).flatMap(([key, item]) => stringFields(item, path ? `${path}.${key}` : key));
  }
  return [];
}

const OWNER_APPROVAL_PATTERN = /todo\s*\(\s*owner-approval\s*\)/i;

/**
 * An approved legal page may not still contain an open owner-approval item
 * anywhere: not in the body and not in any frontmatter value. The check runs
 * over every field of the raw frontmatter, so a field added later is covered
 * without changing this function. Returns the problem, or null.
 */
export function approvedLegalProblem(frontmatter: Record<string, unknown>, body: string): string | null {
  if (frontmatter.status !== 'approved') return null;
  const places = stringFields(frontmatter, '')
    .filter(([, text]) => OWNER_APPROVAL_PATTERN.test(text))
    .map(([path]) => `frontmatter "${path}"`);
  if (OWNER_APPROVAL_PATTERN.test(body)) places.push('the body');
  return places.length
    ? `is marked approved but still contains ${OWNER_APPROVAL_MARKER} in ${places.join(', ')}`
    : null;
}
export type LegalFrontmatter = z.infer<typeof legalFrontmatterSchema>;
